import type { FieldDescriptor } from "./bindings/FieldDescriptor.js";
import type { JsonValue } from "./bindings/serde_json/JsonValue.js";

/**
 * Min/max thinning for large date ranges.
 *
 * Above MAX_POINTS readings per trace, readings are grouped into round,
 * UTC-aligned time buckets and each bucket becomes two rows: one holding every
 * field's minimum (at the bucket start) and one holding every field's maximum
 * (at mid-bucket). Keeping extremes rather than averages or every n-th reading
 * preserves peaks such as gusts and cold snaps.
 *
 * Exceptions: wind direction/stdev are taken at the moments of min/max wind
 * speed (min/max of an angle is meaningless), and rain totals are summed per
 * bucket and repeated on both rows.
 */

export const MAX_POINTS = 2000;

/** Round bucket sizes, smallest first. Buckets align to UTC multiples of these. */
export const BUCKET_SIZES_SECONDS = [
	600, 1200, 1800, 3600, 7200, 10800, 21600, 43200, 86400, 172800, 604800,
];

export type RowKind = "min" | "max";

export interface ThinningInfo {
	bucketSeconds: number;
	rawCount: number;
	rowCount: number;
	hasRainTotal: boolean;
}

type TableData = { [x: string]: JsonValue[] };

export interface ThinResult {
	tableData: TableData;
	/** "min"/"max" per output row, aligned with tableData rows; null when not thinned */
	rowKinds: RowKind[] | null;
	info: ThinningInfo | null;
}

const TIMESTAMP_KEY = "0";

/** Smallest round bucket giving at most maxPoints rows (two per bucket), never finer than sampling */
export function chooseBucketSeconds(
	spanSeconds: number,
	samplingIntervalSeconds: number | null | undefined,
	maxPoints: number,
): number {
	const minSize = Math.max(
		spanSeconds / (maxPoints / 2),
		samplingIntervalSeconds ?? 0,
	);
	return (
		BUCKET_SIZES_SECONDS.find((size) => size >= minSize) ??
		BUCKET_SIZES_SECONDS[BUCKET_SIZES_SECONDS.length - 1]
	);
}

/** Median gap between readings, in seconds; null if it can't be determined */
function medianStepSeconds(times: number[]): number | null {
	const steps: number[] = [];
	for (let i = 1; i < times.length; i++) {
		const step = times[i] - times[i - 1];
		if (step > 0) steps.push(step);
	}
	if (steps.length === 0) return null;
	steps.sort((a, b) => a - b);
	return steps[Math.floor(steps.length / 2)] / 1000;
}

const toNumber = (value: JsonValue | undefined): number | null =>
	typeof value === "number" && !Number.isNaN(value) ? value : null;

export function thinTableData(
	tableData: TableData,
	fieldMeta: FieldDescriptor[] | undefined,
	samplingIntervalSeconds: number | null | undefined,
	maxPoints: number = MAX_POINTS,
	/**
	 * The visible range. The fetcher returns everything cached for the station,
	 * so thinning is decided and sized by the readings in view, then applied to
	 * all of it (keeping double-click zoom-out and panning working as before).
	 */
	view?: { startMs: number; endMs: number },
): ThinResult {
	const unchanged: ThinResult = { tableData, rowKinds: null, info: null };
	const timestamps = (tableData[TIMESTAMP_KEY] ?? []) as string[];
	if (timestamps.length <= maxPoints) return unchanged;

	const times = timestamps.map((t) => Date.parse(t));
	const inView = (t: number): boolean =>
		!view || (t >= view.startMs && t < view.endMs);
	const viewTimes = view ? times.filter(inView) : times;
	const rawCount = viewTimes.length;
	if (rawCount <= maxPoints) return unchanged;
	const spanSeconds = (viewTimes[rawCount - 1] - viewTimes[0]) / 1000;
	// Prefer the actual spacing: the registry interval can be wrong for a source
	// (e.g. Taranaki's 10-minute readings are stored in the "hourly" table 1)
	const intervalSeconds = medianStepSeconds(times) ?? samplingIntervalSeconds;
	const bucketSeconds = chooseBucketSeconds(
		spanSeconds,
		intervalSeconds,
		maxPoints,
	);
	// A bucket no bigger than the reading interval would only duplicate rows
	if (intervalSeconds && bucketSeconds <= intervalSeconds) {
		return unchanged;
	}
	const bucketMs = bucketSeconds * 1000;

	const keyForRole = (role: string): string | undefined => {
		const index = fieldMeta?.findIndex((f) => f.role === role) ?? -1;
		return index >= 0 ? index.toString() : undefined;
	};
	const windSpeedKey = keyForRole("wind_speed");
	const pairedKeys = new Set(
		[keyForRole("wind_direction"), keyForRole("wind_stdev")].filter(
			(k): k is string => k !== undefined,
		),
	);
	const rainKey = keyForRole("rain_total");

	const valueKeys = Object.keys(tableData).filter((k) => k !== TIMESTAMP_KEY);
	const out: TableData = { [TIMESTAMP_KEY]: [] };
	for (const key of valueKeys) out[key] = [];
	const rowKinds: RowKind[] = [];

	const emitBucket = (bucketStart: number, from: number, to: number): void => {
		out[TIMESTAMP_KEY].push(
			new Date(bucketStart).toISOString(),
			new Date(bucketStart + bucketMs / 2).toISOString(),
		);
		rowKinds.push("min", "max");

		// Sample indices of min/max wind speed, used for direction and stdev
		let speedMinIdx = from;
		let speedMaxIdx = to - 1;
		if (windSpeedKey !== undefined) {
			let lo = Number.POSITIVE_INFINITY;
			let hi = Number.NEGATIVE_INFINITY;
			for (let i = from; i < to; i++) {
				const v = toNumber(tableData[windSpeedKey][i]);
				if (v === null) continue;
				if (v < lo) [lo, speedMinIdx] = [v, i];
				if (v > hi) [hi, speedMaxIdx] = [v, i];
			}
		}

		for (const key of valueKeys) {
			const column = tableData[key];
			if (pairedKeys.has(key)) {
				out[key].push(column[speedMinIdx] ?? null, column[speedMaxIdx] ?? null);
				continue;
			}
			let lo: number | null = null;
			let hi: number | null = null;
			let sum = 0;
			for (let i = from; i < to; i++) {
				const v = toNumber(column[i]);
				if (v === null) continue;
				if (lo === null || v < lo) lo = v;
				if (hi === null || v > hi) hi = v;
				sum += v;
			}
			if (key === rainKey) {
				const total = lo === null ? null : sum;
				out[key].push(total, total);
			} else {
				out[key].push(lo, hi);
			}
		}
	};

	// Walk the (time-ordered) readings, emitting each non-empty bucket
	let from = 0;
	while (from < times.length) {
		const bucketStart = Math.floor(times[from] / bucketMs) * bucketMs;
		let to = from + 1;
		while (to < times.length && times[to] < bucketStart + bucketMs) to++;
		emitBucket(bucketStart, from, to);
		from = to;
	}

	return {
		tableData: out,
		rowKinds,
		info: {
			bucketSeconds,
			rawCount,
			rowCount: (out[TIMESTAMP_KEY] as string[]).filter((t) =>
				inView(Date.parse(t)),
			).length,
			hasRainTotal: rainKey !== undefined && rainKey in tableData,
		},
	};
}

export function formatBucket(seconds: number): string {
	const units: [number, string][] = [
		[604800, "week"],
		[86400, "day"],
		[3600, "hour"],
		[60, "minute"],
	];
	for (const [size, name] of units) {
		if (seconds >= size && seconds % size === 0) {
			const count = seconds / size;
			return `${count} ${name}${count === 1 ? "" : "s"}`;
		}
	}
	return `${seconds} seconds`;
}

export function thinningNotice(info: ThinningInfo): string {
	const bucket = formatBucket(info.bucketSeconds);
	const counts = `${info.rawCount.toLocaleString("en-NZ")} readings → ${info.rowCount.toLocaleString("en-NZ")} rows`;
	const rain = info.hasRainTotal
		? ` Rainfall shows the total per ${bucket}.`
		: "";
	return `Showing the minimum and maximum of every ${bucket} (${counts}).${rain} Zoom in or narrow the date range to see every reading.`;
}
